/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, February 1st 2024, 9:32:36 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DataTable, StyledOSDialog, Image, StackedSelectionCard } from "ui-widgets";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { DocumentTemplate, NotificationTemplate } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import { useServiceBuilderContext } from "service-builder/providers";
import { ClueIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";

/**
 *
 * TODO: Seperate the grid selector out into a reusable component
 *
 */

export const AttachmentsInput = () => {
  const { watch, setValue } = useOSFormContext<NotificationTemplate>();
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const selectedDocumentTemplateHexes = watch("attachments");
  const selectedCountText = `${selectedDocumentTemplateHexes?.length} templates selected`;
  const hasSelected = selectedDocumentTemplateHexes?.length > 0;

  const onSelectTemplate = (template: DocumentTemplate) => {
    setValue("attachments", [...(selectedDocumentTemplateHexes ?? []), template.artifact_tag]);
  };

  const onUnSelectTemplate = (template: DocumentTemplate) => {
    setValue(
      "attachments",
      selectedDocumentTemplateHexes?.filter((hex) => hex !== template.artifact_tag)
    );
  };

  const isSelected = (template: DocumentTemplate) => selectedDocumentTemplateHexes?.includes(template.artifact_tag);

  const renderOpenButton = ({ handleOpen }) => {
    const chipComponent = (
      <Chip
        onClick={handleOpen}
        color="secondary"
        variant="outlined"
        icon={<AttachEmailIcon />}
        label={hasSelected ? "Update" : "Add"}
        sx={{ minWidth: "100%" }}
      />
    );

    if (hasSelected) {
      return (
        <Badge badgeContent={selectedCountText} color="primary">
          {chipComponent}
        </Badge>
      );
    }

    return chipComponent;
  };

  return (
    <Stack>
      <StyledOSDialog
        popUpIcon={<AttachEmailIcon color="primary" />}
        maxWidth="sm"
        popUpTitle="Choose a Document Template"
        renderOpenButton={renderOpenButton}
      >
        <div className="os-org-list-wrapper">
          <DataTable<DocumentTemplate>
            title="Document Templates"
            dataSource={APISERVICEPATHS.documentTemplates}
            apiFilterParams={{ process_definition: currentProcessDefinition?.id, include_dangling: true }}
            uniqueKey="id"
            defaultOrderBy="title"
            initialRowsPerPage={10}
            unit="Template"
            titleKey="title"
            searchKeys={["title"]}
            listViewMode="grid"
            renderGridItem={(template) => (
              <StackedSelectionCard
                label={
                  <h4>
                    {template.is_dangling ? (
                      <Tooltip
                        title={
                          <Typography>
                            This document template is not linked to any workflow and adding it as an attachment may
                            prevent the delivery of your message.
                          </Typography>
                        }
                      >
                        <Stack direction="row" columnGap={2}>
                          {template.title} <ClueIcon fill={COLORSCHEME.error} width={25} height={25} />
                        </Stack>
                      </Tooltip>
                    ) : (
                      template.title
                    )}
                  </h4>
                }
                onClick={() => (isSelected(template) ? onUnSelectTemplate(template) : onSelectTemplate(template))}
                actionButton={
                  <IconButton color="primary">
                    {isSelected(template) ? <RemoveCircleIcon /> : <AddCircleIcon />}
                  </IconButton>
                }
                icon={<Image className="image-style-A" src={"/img/document.png"} alt={template.title} />}
                isSelected={isSelected(template)}
              />
            )}
            showToolbar={false}
          />
        </div>
      </StyledOSDialog>
    </Stack>
  );
};
